import { useEffect, useState } from 'react';
import Cookie from 'js-cookie';

import { Box, Button, useDisclosure } from '@chakra-ui/react';

import { mbaAiIndiaNewsItems } from '~/features/accreditation/constants/news-items';
import { AccreditationMilestonesProps, AccreditationPageContentProps } from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { VIEWER_COUNTRY_COOKIE } from '~/features/cookies/constants';
import { publicEnv } from '~/features/environment/public';
import { DownloadIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

import { AccreditationAboutSection } from './accreditation-about-section';
import { AccreditationFaqsSection } from './accreditation-faqs-section';
import { AccreditationFooter } from './accreditation-footer';
import { AccreditationHeroSection } from './accreditation-hero-section';
import { AccreditationMilestonesSection } from './accreditation-milestones-section';
import { AccreditationModal } from './accreditation-modal';
import { AccreditationNewsSection } from './accreditation-news-section';
import { AccreditationPathSection } from './accreditation-path-section';
import { AccreditationPrerequisitesSection } from './accreditation-prerequisites-section';
import { AccreditationResourcesSection } from './accreditation-resources-section';
import { AccreditationStatsSection } from './accreditation-stats-section';
import { AccreditationTestimonialsSection } from './accreditation-testimonials-section';
import { AccreditationTopBar } from './accreditation-top-bar';
import { AccreditationValuesSection } from './accreditation-values-section';

/** The content config supplies the milestones copy; the page supplies that copy plus the Woolf curriculum. */
type AccreditationPageContentComponentProps = Omit<AccreditationPageContentProps, 'milestones'> & {
  milestones: AccreditationMilestonesProps;
};

export function AccreditationPageContent({
  colorScheme = 'blue',
  formalDegreeKey,
  hero,
  topBarItems,
  values,
  stats,
  pathSteps,
  pathTitle,
  pathDescription,
  about,
  prerequisites,
  milestones,
  testimonials,
  footer,
  resources,
  faqs,
  faqsSupportLink,
  newsItems,
}: AccreditationPageContentComponentProps) {
  const brochureUri =
    publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'test' ? 'http://127.0.0.1:3003/public/brochure.pdf' : hero.brochureUri;

  const [hasSubmittedBrochureForm, setHasSubmittedBrochureForm] = useState(false);
  const {
    isOpen: isAccreditationModalOpen,
    onOpen: openAccreditationModal,
    onClose: closeAccreditationModal,
  } = useDisclosure();

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    isEnabled: true,
  });

  const [resolvedNewsItems, setResolvedNewsItems] = useState(newsItems);

  useEffect(() => {
    if (formalDegreeKey === 'mba_ai' && Cookie.get(VIEWER_COUNTRY_COOKIE) === 'IN') {
      setResolvedNewsItems(mbaAiIndiaNewsItems);
    }
  }, [formalDegreeKey]);

  function handleDownloadBrochureClick() {
    if (!hasSubmittedBrochureForm) {
      openAccreditationModal();
    } else {
      window.open(brochureUri, '_blank');
    }
  }

  return (
    <Box overflowX='hidden'>
      <AccreditationHeroSection
        {...hero}
        formalDegreeKey={formalDegreeKey}
        downloadBrochureButtonSlot={
          <Button
            colorScheme='white'
            leftIcon={<DownloadIcon height='18px' width='18px' />}
            size='icon-left'
            variant='outline'
            w={{ base: '100%', md: 'fit-content' }}
            onClick={() => {
              trackEvent({
                type: 'click',
                name: 'Masters Page CTA Clicked',
                label: 'top_learn_more_top',
                additionalAttributes: {
                  formal_degree_key: formalDegreeKey,
                },
              });
              handleDownloadBrochureClick();
            }}
          >
            Download Brochure
          </Button>
        }
      />
      <Box bg='grey.50'>
        <AccreditationTopBar colorScheme={colorScheme} items={topBarItems} />
        <Box h={{ base: '56px', md: '80px' }} />
        <AccreditationValuesSection {...values} />
        <Box h='64px' />
        <AccreditationStatsSection colorScheme={colorScheme} {...stats} />
        <Box h={{ base: '0', md: '120px' }} />
      </Box>
      <Box bg='white'>
        <Box h={{ base: '56px', md: '120px' }} />
        <AccreditationPathSection description={pathDescription} sectionTitle={pathTitle} steps={pathSteps} />
        <Box h={{ base: '56px', md: '120px' }} />
        <AccreditationAboutSection colorScheme={colorScheme} {...about} />
        <Box h={{ base: '56px', md: '120px' }} />
        <AccreditationPrerequisitesSection
          colorScheme={colorScheme}
          formalDegreeKey={formalDegreeKey}
          {...prerequisites}
        />
        <Box h={{ base: '0', md: '120px' }} />
      </Box>
      <Box bg='grey.50'>
        <Box h={{ base: '56px', md: '120px' }} />
        <AccreditationMilestonesSection colorScheme={colorScheme} formalDegreeKey={formalDegreeKey} {...milestones} />
        <Box
          bg='grey.200'
          display={{ base: 'none', md: 'block' }}
          h='1px'
          m='auto'
          my='80px'
          w='content-max-width-md'
        />
        <Box display={{ base: 'block', md: 'none' }} h='72px' />
        {resources && (
          <>
            <AccreditationResourcesSection colorScheme={colorScheme} formalDegreeKey={formalDegreeKey} {...resources} />
            <Box h={{ base: '56px', md: '120px' }} />
          </>
        )}
        <AccreditationTestimonialsSection formalDegreeKey={formalDegreeKey} testimonials={testimonials} />
        <Box h={{ base: '56px', md: '120px' }} />
        {resolvedNewsItems && resolvedNewsItems.length > 0 && (
          <>
            <AccreditationNewsSection
              colorScheme={colorScheme}
              formalDegreeKey={formalDegreeKey}
              items={resolvedNewsItems}
            />
            <Box h={{ base: '56px', md: '120px' }} />
          </>
        )}
      </Box>

      <Box bg='white'>
        <Box h={{ base: '56px', md: '120px' }} />
        <AccreditationFaqsSection
          colorScheme={colorScheme}
          faqs={faqs}
          formalDegreeKey={formalDegreeKey}
          supportLink={faqsSupportLink}
        />
        <Box h={{ base: '56px', md: '140px' }} />
      </Box>
      <AccreditationFooter
        {...footer}
        formalDegreeKey={formalDegreeKey}
        downloadBrochureButtonSlot={
          <Button
            colorScheme='white'
            leftIcon={<DownloadIcon height='18px' width='18px' />}
            size='icon-left'
            variant='outline'
            w={{ base: '100%', md: 'fit-content' }}
            onClick={() => {
              trackEvent({
                type: 'click',
                name: 'Masters Page CTA Clicked',
                label: 'bottom_learn_more',
                additionalAttributes: {
                  formal_degree_key: formalDegreeKey,
                },
              });
              handleDownloadBrochureClick();
            }}
          >
            Download Brochure
          </Button>
        }
      />
      <AccreditationModal
        closeModal={closeAccreditationModal}
        formalDegreeKey={formalDegreeKey}
        isOpen={isAccreditationModalOpen}
        onSuccess={() => {
          window.open(brochureUri, '_blank');
          setHasSubmittedBrochureForm(true);
        }}
      />
    </Box>
  );
}
